import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Settings2, Database, Users, Info, AlertTriangle, RefreshCw, FilePlus2 } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const SEMESTERS = ["I", "II"];
const CLASSES = ["CMT4", "CMT5", "CMT6", "PST4", "PST5", "PST6", "SW4", "SW5", "SW6"];
const EXAM_TYPES = ["CAT I", "CAT II", "ES I", "ES II"];
const ACADEMIC_YEARS = ["2024/2025", "2025/2026"];

export type StudentSource = "pre_registered" | "signed_up";
export type UploadMode = "new" | "reupload";

export interface ResultContext {
  semester: string;
  selectedClass: string;
  examType: string;
  academicYear: string;
  studentSource: StudentSource;
  mode?: UploadMode;
}

interface Props {
  context: ResultContext;
  onChange: (ctx: ResultContext) => void;
}

export function ResultSetup({ context, onChange }: Props) {
  const set = (key: keyof ResultContext, val: string) =>
    onChange({ ...context, [key]: val });

  const isComplete = context.semester && context.selectedClass && context.examType && context.academicYear;

  // Source counts — use comparable filters so the two numbers can be reasoned about.
  // Pre-registered: per (class, semester). Signed-up: per class, split into total vs active-eligible
  // (matching the template's `status ilike '%active%'` filter).
  const { data: sourceCounts } = useQuery({
    queryKey: ["source-counts", context.selectedClass, context.semester],
    enabled: !!context.selectedClass && !!context.semester,
    queryFn: async () => {
      const cls = context.selectedClass;
      const [preRegResult, signedUpAll, signedUpActive] = await Promise.all([
        (supabase as any)
          .from("student_pre_registrations_exam_safe")
          .select("id", { count: "exact", head: true })
          .eq("class", cls)
          .eq("semester", context.semester),
        supabase
          .from("students")
          .select("id", { count: "exact", head: true })
          .eq("class", cls),
        supabase
          .from("students")
          .select("id", { count: "exact", head: true })
          .eq("class", cls)
          .ilike("status", "%active%"),
      ]);
      return {
        preRegistered: preRegResult.count ?? 0,
        signedUp: signedUpAll.count ?? 0,
        signedUpActive: signedUpActive.count ?? 0,
      };
    },
  });

  const showSourceWarning = sourceCounts && isComplete && (
    (context.studentSource === "signed_up" && sourceCounts.signedUpActive === 0 && sourceCounts.preRegistered > 0) ||
    (context.studentSource === "pre_registered" && sourceCounts.preRegistered === 0 && sourceCounts.signedUpActive > 0)
  );

  const alternateSource = context.studentSource === "signed_up" ? "Pre-registered" : "Signed-up";
  const alternateCount = context.studentSource === "signed_up" ? sourceCounts?.preRegistered : sourceCounts?.signedUpActive;

  // Diagnostic: when signed-up active < pre-registered for the same class+semester,
  // explain the most common causes so the user can act on it.
  const showCountDelta = !!sourceCounts && !!isComplete &&
    sourceCounts.preRegistered > 0 &&
    sourceCounts.signedUpActive < sourceCounts.preRegistered;
  const inactiveExcluded = sourceCounts ? Math.max(0, sourceCounts.signedUp - sourceCounts.signedUpActive) : 0;
  const notSignedUp = sourceCounts ? Math.max(0, sourceCounts.preRegistered - sourceCounts.signedUp) : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Settings2 className="h-4 w-4 text-primary" />
          Result Setup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Mode Selector */}
        <div className="space-y-2">
          <Label className="text-xs font-medium flex items-center gap-1.5">
            <RefreshCw className="h-3.5 w-3.5 text-primary" />
            Upload Mode *
          </Label>
          <ToggleGroup
            type="single"
            value={context.mode ?? "new"}
            onValueChange={(v) => {
              if (v) onChange({ ...context, mode: v as UploadMode });
            }}
            className="justify-start"
          >
            <ToggleGroupItem
              value="new"
              className="gap-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-4"
            >
              <FilePlus2 className="h-3.5 w-3.5" />
              New Batch
            </ToggleGroupItem>
            <ToggleGroupItem
              value="reupload"
              className="gap-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-4"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Re-upload (Supp / Repeat / Incomplete)
            </ToggleGroupItem>
          </ToggleGroup>
          <p className="text-[11px] text-muted-foreground flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            {context.mode === "reupload"
              ? "Updates only modules a student previously failed (FAIL / SUPP / INC / REPEAT). Passed modules will be skipped. Existing batch must already exist for the selected Class / Semester / Exam Type / Year."
              : "Creates a fresh draft batch (or replaces an existing draft) for this Class / Semester / Exam Type / Year."}
          </p>
        </div>

        {/* Student Data Source Selector */}
        <div className="space-y-2">
          <Label className="text-xs font-medium flex items-center gap-1.5">
            <Database className="h-3.5 w-3.5 text-primary" />
            Student Data Source *
          </Label>
          <ToggleGroup
            type="single"
            value={context.studentSource}
            onValueChange={(v) => {
              if (v) set("studentSource", v);
            }}
            className="justify-start"
          >
            <ToggleGroupItem
              value="pre_registered"
              className="gap-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-4"
            >
              <Users className="h-3.5 w-3.5" />
              Pre-registered Students
              {sourceCounts && <span className="ml-1 opacity-70">({sourceCounts.preRegistered})</span>}
            </ToggleGroupItem>
            <ToggleGroupItem
              value="signed_up"
              className="gap-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-4"
            >
              <Database className="h-3.5 w-3.5" />
              Signed-up Students
              {sourceCounts && (
                <span className="ml-1 opacity-70">
                  ({sourceCounts.signedUpActive} active{sourceCounts.signedUp !== sourceCounts.signedUpActive ? ` / ${sourceCounts.signedUp} total` : ""})
                </span>
              )}
            </ToggleGroupItem>
          </ToggleGroup>
          <p className="text-[11px] text-muted-foreground flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            {context.studentSource === "pre_registered"
              ? "Uses pre-registered student records. Results can be uploaded before students complete sign-up. Results auto-link when students sign up later."
              : "Uses signed-up student accounts. Students must have completed registration and sign-up."}
          </p>
          {showSourceWarning && (
            <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950 p-2.5 text-xs text-orange-800 dark:text-orange-300">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>0 students</strong> found for the selected source. However, <strong>{alternateCount} {alternateSource} students</strong> exist for {context.selectedClass}. You may need to switch the student source.
              </span>
            </div>
          )}
          {showCountDelta && !showSourceWarning && (
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-2.5 text-xs text-blue-800 dark:text-blue-300">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>{sourceCounts!.signedUpActive}</strong> signed-up active vs <strong>{sourceCounts!.preRegistered}</strong> pre-registered for {context.selectedClass} Sem {context.semester}.
                {" "}Likely cause:{" "}
                {notSignedUp > 0 && <span><strong>{notSignedUp}</strong> not signed up yet</span>}
                {notSignedUp > 0 && inactiveExcluded > 0 && <span>; </span>}
                {inactiveExcluded > 0 && <span><strong>{inactiveExcluded}</strong> with non-active status (excluded from template)</span>}
                {notSignedUp === 0 && inactiveExcluded === 0 && <span>class label whitespace mismatch (e.g. "CMT 4" vs "CMT4")</span>}
                .
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Semester *</Label>
            <Select value={context.semester} onValueChange={(v) => set("semester", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {SEMESTERS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Class *</Label>
            <Select value={context.selectedClass} onValueChange={(v) => set("selectedClass", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Exam Type *</Label>
            <Select value={context.examType} onValueChange={(v) => set("examType", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {EXAM_TYPES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Academic Year *</Label>
            <Select value={context.academicYear} onValueChange={(v) => set("academicYear", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {ACADEMIC_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {isComplete && (
          <div className="mt-1 p-2 rounded-md bg-primary/5 border border-primary/10 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Ready:</span>
            <Badge variant="outline" className="text-[10px]">
              {context.studentSource === "pre_registered" ? "Pre-registered" : "Signed-up"}
            </Badge>
            <span className="font-medium text-foreground">{context.selectedClass}</span>
            &middot; Semester {context.semester} &middot; {context.examType} &middot; {context.academicYear}
          </div>
        )}
      </CardContent>
    </Card>
  );
}